/**
 * Snack Madness Sweepstakes – frontend config.
 * Contest dates come from the sweepstakes table (open_date, close_date); this file only has the tag and helpers.
 */

/** Hardcoded sweepstakes tag for querying the correct sweepstakes from the API (sweepstakes.sweepstakes_tag). Reusable each year with a new row in sweepstakes table. */
export const SNACK_MADNESS_SWEEPSTAKES_TAG = 'snack-madness-2026';

/** Terms & Conditions route path — use with LinkWithCountry so link is correct per deployment (staging/prod). */
export const SNACK_MADNESS_TERMS_PATH = '/snack-madness-sweeps-terms-and-conditions';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Parse date string from API as UTC (backend sends e.g. "2026-03-15 04:00:00" meaning UTC).
 * Without this, JS parses "2026-03-15 04:00:00" as local time and getUTCDate() can be off by one.
 * @param {string} dateStr - e.g. "2026-03-15 04:00:00" or "2026-03-15T04:00:00.000Z"
 * @returns {Date}
 */
const parseDateAsUTC = (dateStr) => {
  if (!dateStr) return new Date(NaN);
  const s = String(dateStr).trim();
  if (s.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
  const iso = `${s.replace(' ', 'T')  }Z`;
  return new Date(iso);
};
export const getOrdinalSuffix = (day) => {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

/**
 * Label for pre-contest state from open date (e.g. "Contest begins March 15th").
 * @param {string} openDate - ISO date string (e.g. from sweepstakes.open_date)
 * @returns {string}
 */
export const getContestStartLabel = (openDate) => {
  if (!openDate) return 'Contest begins soon';
  const d = parseDateAsUTC(openDate);
  const month = MONTH_NAMES[d.getUTCMonth()];
  const day = d.getUTCDate();
  const suffix = getOrdinalSuffix(day);
  return `Contest begins ${month} ${day}${suffix}`;
};

/**
 * True when current time is within contest window [openDate, closeDate).
 * @param {string} openDate - ISO date string
 * @param {string} closeDate - ISO date string
 */
export const isContestOpen = (openDate, closeDate) => {
  if (!openDate || !closeDate) return false;
  const now = Date.now();
  const start = parseDateAsUTC(openDate).getTime();
  const end = parseDateAsUTC(closeDate).getTime();
  return now >= start && now < end;
};

/**
 * True when contest start (open) date has passed.
 * @param {string} openDate - ISO date string
 */
export const isContestStarted = (openDate) => {
  if (!openDate) return false;
  return Date.now() >= parseDateAsUTC(openDate).getTime();
};

/**
 * Contest has ended (current time >= close_date).
 * @param {string} closeDate - ISO date string
 */
export const isContestClosed = (closeDate) => {
  if (!closeDate) return false;
  return Date.now() >= parseDateAsUTC(closeDate).getTime();
};
